import { useEffect } from "react";
import type { AnchorRect } from "./hooks/useTourAnchor";

interface TourSpotlightProps {
  rect: AnchorRect | null;
  /** When true → smooth scroll the target into view on first measure */
  scrollIntoView?: boolean;
  element?: HTMLElement | null;
}

/**
 * Soft backdrop + glow ring that highlights the active tour target.
 * - Backdrop: very subtle dark wash (8% opacity) — non-blocking, pointer-events disabled
 * - Spotlight: rounded ring with primary glow around the target rect
 * - No interaction blocking: the user can still click anywhere
 */
export const TourSpotlight = ({ rect, scrollIntoView = true, element }: TourSpotlightProps) => {
  // Smooth-scroll target into view when it first resolves
  useEffect(() => {
    if (!scrollIntoView || !element) return;
    // Defer to next tick so the tooltip can position
    const timer = setTimeout(() => {
      element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }, 50);
    return () => clearTimeout(timer);
  }, [element, scrollIntoView]);

  if (!rect) return null;

  const padding = 8;
  const top = rect.top - padding;
  const left = rect.left - padding;
  const width = rect.width + padding * 2;
  const height = rect.height + padding * 2;

  return (
    <>
      {/* Glow ring around the target — no backdrop, no blur, no pulse */}
      <div
        className="fixed z-30 pointer-events-none rounded-xl transition-all duration-300 ease-out"
        style={{
          top,
          left,
          width,
          height,
          boxShadow:
            "0 0 0 2px hsl(var(--primary)), 0 0 24px 4px hsl(var(--primary) / 0.45), inset 0 0 0 1px hsl(var(--primary) / 0.4)",
        }}
        aria-hidden
      />
    </>
  );
};
